import type { Vec3 } from './math.js';

/**
 * Heightfield terrain: a regular grid of heights sampled bilinearly. Deterministic on client and
 * server. Cells are `cellSize` metres; heights[row * cols + col] is the height at
 * (originX + col * cellSize, originZ + row * cellSize). Outside the grid the edge value is used.
 */
export interface Heightfield {
  originX: number;
  originZ: number;
  cellSize: number;
  cols: number;
  rows: number;
  heights: readonly number[];
}

export function heightAt(t: Heightfield, x: number, z: number): number {
  const fx = (x - t.originX) / t.cellSize;
  const fz = (z - t.originZ) / t.cellSize;
  const cx = Math.min(t.cols - 2, Math.max(0, Math.floor(fx)));
  const cz = Math.min(t.rows - 2, Math.max(0, Math.floor(fz)));
  const u = Math.min(1, Math.max(0, fx - cx));
  const v = Math.min(1, Math.max(0, fz - cz));
  const i = cz * t.cols + cx;
  const h00 = t.heights[i] ?? 0;
  const h10 = t.heights[i + 1] ?? h00;
  const h01 = t.heights[i + t.cols] ?? h00;
  const h11 = t.heights[i + t.cols + 1] ?? h00;
  const a = h00 + (h10 - h00) * u;
  const b = h01 + (h11 - h01) * u;
  return a + (b - a) * v;
}

/** Surface normal (unnormalised gradient is fine for slope tests). */
export function slopeAt(t: Heightfield, x: number, z: number): { dx: number; dz: number } {
  const e = t.cellSize * 0.5;
  return {
    dx: (heightAt(t, x + e, z) - heightAt(t, x - e, z)) / (2 * e),
    dz: (heightAt(t, x, z + e) - heightAt(t, x, z - e)) / (2 * e),
  };
}

/**
 * Ray march against the heightfield. Returns the distance to the first point under the surface
 * (refined by bisection) or Infinity. Steps at half a cell; deterministic.
 */
export function rayHeightfield(t: Heightfield, o: Vec3, d: Vec3, maxDist: number): number {
  const step = t.cellSize * 0.5;
  let prev = 0;
  let prevAbove = o.y - heightAt(t, o.x, o.z) >= 0;
  if (!prevAbove) return 0;
  for (let s = step; s <= maxDist + step; s += step) {
    const dist = Math.min(s, maxDist);
    const px = o.x + d.x * dist;
    const py = o.y + d.y * dist;
    const pz = o.z + d.z * dist;
    const above = py - heightAt(t, px, pz) >= 0;
    if (!above) {
      // bisect between prev and dist
      let lo = prev;
      let hi = dist;
      for (let k = 0; k < 8; k++) {
        const mid = (lo + hi) / 2;
        const my = o.y + d.y * mid - heightAt(t, o.x + d.x * mid, o.z + d.z * mid);
        if (my >= 0) lo = mid;
        else hi = mid;
      }
      return hi;
    }
    prev = dist;
    prevAbove = above;
    if (dist >= maxDist) break;
  }
  return Infinity;
}

/** Build a heightfield from a function; handy for procedural dunes and tests. */
export function buildHeightfield(
  originX: number,
  originZ: number,
  cellSize: number,
  cols: number,
  rows: number,
  fn: (x: number, z: number) => number,
): Heightfield {
  const heights: number[] = new Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      // Round to cm so the grid is exactly reproducible on every engine.
      heights[r * cols + c] =
        Math.round(fn(originX + c * cellSize, originZ + r * cellSize) * 100) / 100;
    }
  }
  return { originX, originZ, cellSize, cols, rows, heights };
}
